import { Controller } from "@hotwired/stimulus"

const RECENTS_KEY = "emailpa-recent-searches"
const RECENTS_MAX = 10
// Per-type on/off from the gear popover. A display preference, so it lives
// client-side; it's mirrored into a cookie of the same name so a cold
// navigation to /search honours it too (see UniversalSearchController).
const TYPES_KEY = "emailpa-search-types"
const TYPES_COOKIE = "search_types"
const TYPES_COOKIE_MAX_AGE = 60 * 60 * 24 * 365

const SEARCHABLE_TYPES = ["tasks", "people", "companies", "messages", "events", "apps"]

// Placeholder + empty-state copy per chip, so the box always names what it's
// about to look through.
const TYPE_COPY = {
    all: {
        placeholder: "Search tasks, people, companies, messages, events, and apps",
        hint: "Search your tasks, people, companies, messages, events, and apps"
    },
    tasks: { placeholder: "Search tasks", hint: "Find an open task by its title" },
    people: { placeholder: "Search people", hint: "Find anyone you or your team knows" },
    companies: { placeholder: "Search companies", hint: "Find a company in your network" },
    messages: { placeholder: "Search messages", hint: "Find an email or a chat" },
    events: { placeholder: "Search events", hint: "Find something on your calendar" },
    apps: { placeholder: "Search apps", hint: "Find a Jane app by name" },
    actions: { placeholder: "Search quick actions", hint: "Nothing to jump to by that name" }
}

export default class extends Controller {
    static targets = ["backdrop", "dialog", "input", "results", "empty", "chip", "gear", "filters", "toggle"]
    static values = { url: String }

    connect() {
        this._selectedIndex = -1
        this._items = []
        this._debounceTimer = null
        this._type = "all"
        this._actions = this._readActions()
        this._enabled = this._readEnabledTypes()
        this._syncToggles()
        this._syncChips()
        this._onDocKeydown = (event) => this._handleGlobalKeydown(event)
        this._onDocPointerdown = (event) => this._closeFiltersOnOutsideClick(event)
        document.addEventListener("keydown", this._onDocKeydown)
        document.addEventListener("mousedown", this._onDocPointerdown)
    }

    disconnect() {
        document.removeEventListener("keydown", this._onDocKeydown)
        document.removeEventListener("mousedown", this._onDocPointerdown)
        this._clearDebounce()
        this.close()
    }

    open(event) {
        if (event) event.preventDefault()
        if (!this.hasBackdropTarget) return

        this.backdropTarget.classList.remove("hidden")
        this.backdropTarget.setAttribute("aria-hidden", "false")
        document.body.classList.add("universal-search-open")
        this.inputTarget.value = ""
        this._setType("all", { refocus: false })
        this._renderIdleState()
        window.requestAnimationFrame(() => this.inputTarget.focus())
    }

    close() {
        if (!this.hasBackdropTarget || this.backdropTarget.classList.contains("hidden")) return

        this._closeFilters()
        this.backdropTarget.classList.add("hidden")
        this.backdropTarget.setAttribute("aria-hidden", "true")
        document.body.classList.remove("universal-search-open")
        this._clearDebounce()
        this._selectedIndex = -1
        this._items = []
    }

    closeOnBackdrop(event) {
        if (event.target === this.backdropTarget) this.close()
    }

    // Persistent "New message" row: close search, hand off to the messages
    // drawer's own composer via a window event (messages-sidebar listens for
    // "jane:compose") — this panel doesn't need to know the drawer exists,
    // only that something else is listening.
    composeNew() {
        this.close()
        window.dispatchEvent(new CustomEvent("jane:compose"))
    }

    stopPropagation(event) {
        event.stopPropagation()
    }

    query() {
        this._clearDebounce()
        this._debounceTimer = window.setTimeout(() => this._fetchResults(), 180)
    }

    inputKeydown(event) {
        if (event.key === "ArrowDown") {
            event.preventDefault()
            this._moveSelection(1)
        } else if (event.key === "ArrowUp") {
            event.preventDefault()
            this._moveSelection(-1)
        } else if (event.key === "Enter") {
            event.preventDefault()
            this._activateSelection()
        } else if (event.key === "Escape") {
            event.preventDefault()
            event.stopPropagation()
            // The popover is the innermost layer: Escape closes it first, and
            // only a second Escape closes the modal.
            if (this._filtersOpen()) this._closeFilters()
            else this.close()
        }
    }

    _handleGlobalKeydown(event) {
        let mod = event.metaKey || event.ctrlKey
        if (mod && event.key.toLowerCase() === "k") {
            event.preventDefault()
            if (this.backdropTarget.classList.contains("hidden")) this.open()
            else this.close()
            return
        }

        if (event.key === "Escape" && !this.backdropTarget.classList.contains("hidden")) {
            event.preventDefault()
            if (this._filtersOpen()) this._closeFilters()
            else this.close()
        }
    }

    // ---- Type chips ----------------------------------------------------

    selectType(event) {
        event.preventDefault()
        let type = event.currentTarget.dataset.type || "all"
        this._setType(type)
        if (this.inputTarget.value.trim() === "") this._renderIdleState()
        else this._fetchResults()
    }

    _setType(type, { refocus = true } = {}) {
        this._type = type
        this._syncChips()
        let copy = TYPE_COPY[type] || TYPE_COPY.all
        this.inputTarget.placeholder = copy.placeholder
        if (refocus) this.inputTarget.focus()
    }

    _syncChips() {
        if (!this.hasChipTarget) return

        this.chipTargets.forEach((chip) => {
            let type = chip.dataset.type
            let active = type === this._type
            chip.classList.toggle("is-active", active)
            chip.setAttribute("aria-pressed", active ? "true" : "false")
            // A type switched off in the popover reads as de-emphasised, but
            // stays clickable — clicking it is the fastest way to look anyway.
            let off = SEARCHABLE_TYPES.includes(type) && !this._enabled.includes(type)
            chip.classList.toggle("is-off", off)
        })
    }

    // ---- Filter popover ------------------------------------------------

    toggleFilters(event) {
        event?.preventDefault()
        if (this._filtersOpen()) this._closeFilters()
        else this._openFilters()
    }

    toggleType(event) {
        let type = event.currentTarget.dataset.type
        if (!SEARCHABLE_TYPES.includes(type)) return

        let next = event.currentTarget.checked ? this._enabled.concat([type]) : this._enabled.filter((t) => t !== type)
        // Never let the last one go dark — an omnibox that searches nothing is
        // a bug report, not a preference.
        if (next.length === 0) {
            event.currentTarget.checked = true
            return
        }
        this._writeEnabledTypes(next)
        this._syncToggles()
        this._syncChips()
        this._refresh()
    }

    resetFilters(event) {
        event?.preventDefault()
        this._writeEnabledTypes(SEARCHABLE_TYPES)
        this._syncToggles()
        this._syncChips()
        this._refresh()
    }

    // Tab out of the popover, or Escape inside it, closes it and hands focus
    // back to the input so typing keeps working.
    filtersKeydown(event) {
        if (event.key !== "Escape") return

        event.preventDefault()
        event.stopPropagation()
        this._closeFilters()
    }

    _filtersOpen() {
        return this.hasFiltersTarget && !this.filtersTarget.classList.contains("hidden")
    }

    _openFilters() {
        if (!this.hasFiltersTarget) return

        this.filtersTarget.classList.remove("hidden")
        // The modal clips its own overflow, so a short result list would cut the
        // popover off; the class reserves just enough body height while it's up.
        if (this.hasDialogTarget) this.dialogTarget.classList.add("is-filters-open")
        this.gearTarget?.setAttribute("aria-expanded", "true")
        window.requestAnimationFrame(() => this.toggleTargets[0]?.focus())
    }

    _closeFilters() {
        if (!this.hasFiltersTarget) return

        this.filtersTarget.classList.add("hidden")
        if (this.hasDialogTarget) this.dialogTarget.classList.remove("is-filters-open")
        this.gearTarget?.setAttribute("aria-expanded", "false")
    }

    _closeFiltersOnOutsideClick(event) {
        if (!this._filtersOpen()) return
        if (this.filtersTarget.contains(event.target)) return
        if (this.hasGearTarget && this.gearTarget.contains(event.target)) return

        this._closeFilters()
    }

    _syncToggles() {
        this.toggleTargets.forEach((input) => {
            input.checked = this._enabled.includes(input.dataset.type)
        })
    }

    _refresh() {
        if (this.backdropTarget.classList.contains("hidden")) return

        if (this.inputTarget.value.trim() === "") this._renderIdleState()
        else this._fetchResults()
    }

    // ---- Fetch + render ------------------------------------------------

    async _fetchResults() {
        let query = this.inputTarget.value.trim()
        if (query === "") {
            this._renderIdleState()
            return
        }

        // Quick actions are a local list — filtering them needs no round-trip.
        let actions = this._matchingActions(query)
        if (this._type === "actions") {
            this._renderGroups([])
            actions.forEach((action, index) => {
                if (index === 0) this.resultsTarget.appendChild(this._groupLabel("QUICK ACTIONS"))
                this._appendRow(action, { action: true })
            })
            if (actions.length === 0) this._showEmpty(TYPE_COPY.actions.hint)
            else this._hideEmpty()
            return
        }

        this._showEmpty("Searching…")
        try {
            let response = await fetch(`${this.urlValue}?${this._searchParams(query)}`, {
                headers: { Accept: "application/json" },
                credentials: "same-origin"
            })
            if (!response.ok) throw new Error("search failed")
            let payload = await response.json()
            let groups = payload.groups || []
            this._renderGroups(groups, { actions: actions })
            if (groups.length === 0 && actions.length === 0) {
                this._showEmpty("No results")
            } else {
                this._hideEmpty()
                this._appendSeeAllFooter(query)
            }
        } catch (_error) {
            this._renderGroups([])
            this._showEmpty("Could not search right now")
        }
    }

    // `type` carries the active chip; `types` carries the popover's on-switches
    // so a filtered-out type is never queried in the first place.
    _searchParams(query) {
        let params = new URLSearchParams({ q: query })
        if (this._type !== "all") params.set("type", this._type)
        else params.set("types", this._enabled.join(","))
        return params.toString()
    }

    _renderGroups(groups, { actions = [] } = {}) {
        this._selectedIndex = -1
        this._items = []
        this.resultsTarget.replaceChildren()

        if (actions.length > 0) {
            this.resultsTarget.appendChild(this._groupLabel("QUICK ACTIONS"))
            actions.forEach((action) => this._appendRow(action, { action: true }))
        }
        groups.forEach((group) => {
            this.resultsTarget.appendChild(this._groupLabel(group.label))
            group.items.forEach((item) => this._appendRow(item))
        })
    }

    // Idle (empty query): quick actions first — they're the "I came here to do
    // something" path — then recently-opened results. Called on open, on a chip
    // change, and whenever the box is cleared.
    _renderIdleState() {
        let actions = this._type === "all" || this._type === "actions" ? this._actions : []
        let recents = this._type === "actions" ? [] : this._recents()

        this._selectedIndex = -1
        this._items = []
        this.resultsTarget.replaceChildren()

        if (actions.length > 0) {
            this.resultsTarget.appendChild(this._groupLabel("QUICK ACTIONS"))
            actions.forEach((action) => this._appendRow(action, { action: true }))
        }
        if (recents.length > 0) {
            this.resultsTarget.appendChild(this._groupLabel("RECENT", { clearable: true }))
            recents.forEach((item) => this._appendRow(item))
        }

        if (this._items.length === 0) this._showEmpty((TYPE_COPY[this._type] || TYPE_COPY.all).hint)
        else this._hideEmpty()
    }

    _groupLabel(text, { clearable = false } = {}) {
        let label = document.createElement("div")
        label.className = "universal-search-group-label"
        let span = document.createElement("span")
        span.textContent = text
        label.appendChild(span)
        if (clearable) {
            let clear = document.createElement("button")
            clear.type = "button"
            clear.className = "universal-search-clear"
            clear.textContent = "Clear"
            clear.addEventListener("click", (event) => {
                event.preventDefault()
                this._clearRecents()
                this._renderIdleState()
                this.inputTarget.focus()
            })
            label.appendChild(clear)
        }
        return label
    }

    _appendRow(item, { action = false } = {}) {
        let row = document.createElement("a")
        row.className = action ? "universal-search-item universal-search-item--action" : "universal-search-item"
        row.href = item.url
        row.setAttribute("role", "option")
        row.dataset.index = this._items.length.toString()
        row.innerHTML = `
            <span class="universal-search-item-icon" aria-hidden="true"><i class="bi bi-${item.icon || "search"}"></i></span>
            <span class="universal-search-item-body">
                <span class="universal-search-item-name"></span>
                <span class="universal-search-item-subtitle"></span>
            </span>
            <i class="bi bi-chevron-right universal-search-item-chevron" aria-hidden="true"></i>`
        row.querySelector(".universal-search-item-name").textContent = item.name || ""
        row.querySelector(".universal-search-item-subtitle").textContent = item.subtitle || ""
        if (item.image) this._applyAvatar(row.querySelector(".universal-search-item-icon"), item)
        row.addEventListener("mouseenter", () => this._setSelection(parseInt(row.dataset.index, 10)))
        // Enter also triggers .click(), so this covers both activation paths.
        // Quick actions are shortcuts, not results, so they stay out of recents.
        if (!action) row.addEventListener("click", () => this._pushRecent(item))
        this.resultsTarget.appendChild(row)
        this._items.push(row)
    }

    // Swap the icon glyph for the avatar/logo image; on load error (expired
    // CDN URL, missing favicon) fall back to the glyph that's already there.
    _applyAvatar(iconSpan, item) {
        let img = document.createElement("img")
        img.className = "universal-search-item-avatar"
        img.src = item.image
        img.alt = ""
        img.referrerPolicy = "no-referrer"
        img.loading = "lazy"
        img.decoding = "async"
        img.addEventListener("load", () => iconSpan.classList.add("has-avatar"))
        img.addEventListener("error", () => img.remove())
        iconSpan.appendChild(img)
    }

    _moveSelection(delta) {
        if (this._items.length === 0) return
        let next = this._selectedIndex + delta
        if (next < 0) next = this._items.length - 1
        if (next >= this._items.length) next = 0
        this._setSelection(next)
    }

    _setSelection(index) {
        this._items.forEach((row, i) => row.classList.toggle("is-active", i === index))
        this._selectedIndex = index
        this._items[index]?.scrollIntoView({ block: "nearest" })
    }

    _activateSelection() {
        if (this._selectedIndex >= 0 && this._items[this._selectedIndex]) {
            this._items[this._selectedIndex].click()
            return
        }
        // Nothing highlighted: Enter goes to the full results page.
        this._openResultsPage()
    }

    _openResultsPage() {
        let query = this.inputTarget.value.trim()
        if (query === "" || this._type === "actions") return
        window.location.href = this._resultsPageUrl(query)
    }

    _resultsPageUrl(query) {
        let params = new URLSearchParams({ q: query })
        if (this._type !== "all") params.set("type", this._type)
        if (this._enabled.length !== SEARCHABLE_TYPES.length) params.set("types", this._enabled.join(","))
        return `/search?${params.toString()}`
    }

    // A persistent footer row to the full results page — the omnibox previews;
    // the page has every hit as a full card with filters.
    _appendSeeAllFooter(query) {
        let footer = document.createElement("a")
        footer.className = "universal-search-seeall"
        footer.href = this._resultsPageUrl(query)
        let label = document.createElement("span")
        label.textContent = `See all results for “${query}”`
        let icon = document.createElement("i")
        icon.className = "bi bi-arrow-right"
        footer.append(label, icon)
        this.resultsTarget.appendChild(footer)
    }

    _showEmpty(text) {
        if (!this.hasEmptyTarget) return
        this.emptyTarget.hidden = false
        this.emptyTarget.textContent = text
    }

    _hideEmpty() {
        if (!this.hasEmptyTarget) return
        this.emptyTarget.hidden = true
    }

    _clearDebounce() {
        if (this._debounceTimer) window.clearTimeout(this._debounceTimer)
        this._debounceTimer = null
    }

    // ---- Quick actions -------------------------------------------------

    // Server-rendered onto the backdrop: the list is already filtered to what
    // this user can actually do (see UniversalSearch::QuickActions).
    _readActions() {
        try {
            let list = JSON.parse(this.backdropTarget?.dataset.actions || "[]")
            return Array.isArray(list) ? list.filter((a) => a && a.url && a.name) : []
        } catch (_error) {
            return []
        }
    }

    _matchingActions(query) {
        if (this._type !== "all" && this._type !== "actions") return []

        let needle = query.toLowerCase()
        return this._actions.filter((action) => {
            return `${action.name} ${action.subtitle || ""}`.toLowerCase().includes(needle)
        })
    }

    // ---- Persisted preferences -----------------------------------------

    _readEnabledTypes() {
        try {
            let raw = window.localStorage.getItem(TYPES_KEY)
            if (raw === null) return SEARCHABLE_TYPES.slice()
            let list = JSON.parse(raw)
            let clean = Array.isArray(list) ? list.filter((t) => SEARCHABLE_TYPES.includes(t)) : []
            return clean.length > 0 ? clean : SEARCHABLE_TYPES.slice()
        } catch (_error) {
            return SEARCHABLE_TYPES.slice()
        }
    }

    _writeEnabledTypes(types) {
        // Keep nav order regardless of click order, so the cookie and the chips
        // read the same way every time.
        this._enabled = SEARCHABLE_TYPES.filter((t) => types.includes(t))
        try {
            window.localStorage.setItem(TYPES_KEY, JSON.stringify(this._enabled))
        } catch (_error) {
            // localStorage unavailable (private mode / quota) — the cookie below
            // still carries the preference for this session.
        }
        try {
            document.cookie = `${TYPES_COOKIE}=${encodeURIComponent(this._enabled.join(","))}; path=/; max-age=${TYPES_COOKIE_MAX_AGE}; SameSite=Lax`
        } catch (_error) {
            // ignore
        }
    }

    // Recently-opened results, stored locally so they load instantly with no
    // server round-trip. Each is a slimmed copy of the result item.
    _recents() {
        try {
            let raw = window.localStorage.getItem(RECENTS_KEY)
            let list = raw ? JSON.parse(raw) : []
            return Array.isArray(list) ? list.filter((i) => i && i.url && i.name) : []
        } catch (_error) {
            return []
        }
    }

    _pushRecent(item) {
        try {
            let entry = { name: item.name, subtitle: item.subtitle, url: item.url, icon: item.icon, image: item.image }
            let list = this._recents().filter((i) => i.url !== entry.url)
            list.unshift(entry)
            window.localStorage.setItem(RECENTS_KEY, JSON.stringify(list.slice(0, RECENTS_MAX)))
        } catch (_error) {
            // localStorage unavailable (private mode / quota) — recents are a
            // nicety, so silently skip.
        }
    }

    _clearRecents() {
        try {
            window.localStorage.removeItem(RECENTS_KEY)
        } catch (_error) {
            // ignore
        }
    }
}
